import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import {
  useInternalEventImportExportRetrieve,
  useInternalEventTemplateDownload,
  useInternalEventTemplateEventTableDataRetrieveSuspense,
  useInternalEventTemplateVersionRetrieveSuspense,
} from "gen/endpoints/internal/internal";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { EventStatsData } from "gen/model/eventStatsData";
import { compact, noop } from "lodash-es";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxStats } from "shared/components/table/box/TableBoxStats";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { useToast } from "shared/components/toast/useToast";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { rootRoute } from "shared/navigation/Routes";
import { downloadFileBlob } from "shared/utils/utils";

interface InternalTemplateDetailsProps {
  templateVersionId: string;
  templateImportExportId?: string;
  loadStats?: EventStatsData;
  includeDraftEvents: boolean;
}

export const InternalTemplateDetails: React.FC<
  InternalTemplateDetailsProps
> = ({
  templateVersionId,
  templateImportExportId,
  loadStats,
  includeDraftEvents,
}) => {
  const { t } = useTranslation(["common", "internal"]);
  const { showToast, showErrorToast } = useToast();

  const { data: importExportData } = useInternalEventImportExportRetrieve(
    templateImportExportId!,
    {
      query: {
        enabled: !!templateImportExportId,
      },
    },
  );

  const { data: templateData } =
    useInternalEventTemplateVersionRetrieveSuspense(templateVersionId);

  const { mutateAsync: downloadEventTemplate } =
    useInternalEventTemplateDownload({
      request: {
        timeout: 60_000,
      },
    });

  const downloadTemplate = async () => {
    try {
      const fileDownload = await downloadEventTemplate({
        eventTemplateVersionId: templateVersionId,
      });
      const date = new Date().toISOString().slice(0, 10);
      const fileName = templateData.name + date + ".json";
      downloadFileBlob(fileDownload, fileName);
      showToast({
        message: t("common:downloadSuccess"),
        variant: "success",
      });
    } catch (err) {
      showErrorToast(err);
    }
  };
  const { data: tableData, refetch: refetchTableData } =
    useInternalEventTemplateEventTableDataRetrieveSuspense(templateVersionId, {
      event_import_export_id: templateImportExportId,
      include_draft_events: includeDraftEvents,
    });

  const workflowStatuses = useCeleryWorkflowPoll(
    compact([importExportData?.celery_workflow_id]),
    noop,
    refetchTableData,
    refetchTableData,
  );

  const isWorkflowError =
    !!importExportData?.celery_workflow_id &&
    workflowStatuses.get(importExportData.celery_workflow_id)?.state ===
      CeleryWorkflowStateMachineFieldsEnum.failed;

  const workflowErrorMessage =
    !!importExportData?.celery_workflow_id &&
    workflowStatuses.get(importExportData.celery_workflow_id)?.error_message;

  const isWorkflowInProgress =
    !!importExportData?.celery_workflow_id &&
    workflowStatuses.get(importExportData.celery_workflow_id)?.state ===
      CeleryWorkflowStateMachineFieldsEnum.processing;

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) =>
      row.data?.metadata?.type === "id_row"
        ? {
            type: "generic",
            to: rootRoute({})
              .internal({})
              .shadowTemplate({ templateVersionId })
              .templateEvent({
                templateEventId: row.data.metadata.id,
              }).$,
          }
        : undefined,
    [templateVersionId],
  );

  return (
    <>
      {isWorkflowError && (
        <Alert
          data-ls-enabled={true}
          data-public={true}
          status="error"
          margin="8px 0px 8px 0px"
        >
          <AlertIcon>
            <InfoCircleIcon />
          </AlertIcon>
          <AlertTitle flexGrow="0">{t("common:error.generic")}</AlertTitle>
          <AlertDescription>{workflowErrorMessage}</AlertDescription>
        </Alert>
      )}
      <TableBox>
        {loadStats && (
          <TableBoxStats>
            <StatGroup justifyContent="flex-start">
              {loadStats.stats.map((stat, i) => (
                <Stat size="xs" key={`overall_stat_${i}`}>
                  <StatLabel>{stat.rich_text}</StatLabel>
                  <StatNumber>{stat.value}</StatNumber>
                </Stat>
              ))}
            </StatGroup>
          </TableBoxStats>
        )}
        <TableBoxHeader>
          <TableBoxTitle>{t("common:events")}</TableBoxTitle>
          <DataTableBoxActions>
            <Button size="xs" onClick={downloadTemplate}>
              {t("internal:downloadTemplate")}
            </Button>
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={tableData}
            isLoading={isWorkflowInProgress}
            getLinkInfoForRow={getLinkInfoForRow}
          />
        </TableBoxBody>
      </TableBox>
    </>
  );
};
